import React, { useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Stethoscope,
  Pill,
  FlaskConical,
  Scale,
  HeartPulse,
  CigaretteOff,
} from 'lucide-react';
import { telehealthServices } from '../data/telehealthServices';
import './Services.css';

const iconComponents = {
  stethoscope: Stethoscope,
  pill: Pill,
  flask: FlaskConical,
  scale: Scale,
  heartpulse: HeartPulse,
  cigaretteoff: CigaretteOff,
};

const accentColors = ['#1a365d', '#2b6cb0', '#2f855a', '#805ad5', '#c05621', '#b83280'];

const Services = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const services = useMemo(
    () =>
      telehealthServices.map((s, i) => ({
        title: s.title,
        path: `/services/${s.slug}`,
        backgroundImage: s.cardImage,
        color: accentColors[i % accentColors.length],
        icon: iconComponents[s.icon] ? (
          React.createElement(iconComponents[s.icon], { className: 'service-icon' })
        ) : null,
        fee: s.fee,
        feeDetail: s.feeDetail,
      })),
    []
  );

  const duplicatedServices = [...services, ...services];

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (el.__carouselCleanup) el.__carouselCleanup();

    const STEP_PX = 1;          // pixels per tick
    const STEP_MS = 16;         // ~60 fps
    let intervalId = null;
    let paused = false;
    let cancelled = false;
    let resumeTimer = null;
    let pointerDownX = 0;
    let pointerDownScroll = 0;
    let hasDragged = false;

    const step = () => {
      if (cancelled || paused) return;
      const half = el.scrollWidth / 2;
      if (half <= 0) return;
      let next = el.scrollLeft + STEP_PX;
      if (next >= half) next -= half;
      el.scrollLeft = next;
    };

    // Keep scroll position within the first copy so the loop feels infinite
    const handleScroll = () => {
      const half = el.scrollWidth / 2;
      if (half <= 0) return;
      if (el.scrollLeft >= half) el.scrollLeft -= half;
      else if (el.scrollLeft < 0) el.scrollLeft += half;
    };

    const pause = () => {
      paused = true;
      if (resumeTimer) {
        clearTimeout(resumeTimer);
        resumeTimer = null;
      }
    };

    const scheduleResume = () => {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        paused = false;
      }, 1500);
    };

    let mouseDragging = false;

    const onPointerMove = (e) => {
      if (!mouseDragging || e.pointerType !== 'mouse') return;
      const dx = e.clientX - pointerDownX;
      if (Math.abs(dx) > 8) hasDragged = true;
      if (hasDragged) {
        el.scrollLeft = pointerDownScroll - dx;
        e.preventDefault();
      }
    };

    const onPointerUp = (e) => {
      if (mouseDragging && e.pointerType === 'mouse') {
        mouseDragging = false;
        el.classList.remove('dragging');
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerUp);
        document.removeEventListener('pointercancel', onPointerUp);
      }
      const dx = Math.abs(e.clientX - pointerDownX);
      if (dx > 8) hasDragged = true;
      scheduleResume();
    };

    const onPointerDown = (e) => {
      pause();
      pointerDownX = e.clientX;
      pointerDownScroll = el.scrollLeft;
      hasDragged = false;
      if (e.pointerType === 'mouse') {
        mouseDragging = true;
        el.classList.add('dragging');
        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
        document.addEventListener('pointercancel', onPointerUp);
      }
    };

    const onClickCapture = (e) => {
      if (hasDragged) {
        e.stopPropagation();
        e.preventDefault();
        hasDragged = false;
      }
    };

    const onTouchStart = () => pause();
    const onTouchEnd = () => scheduleResume();

    el.scrollLeft = 0;
    intervalId = setInterval(step, STEP_MS);

    el.addEventListener('scroll', handleScroll, { passive: true });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('click', onClickCapture, true);
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });

    const cleanup = () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      if (resumeTimer) clearTimeout(resumeTimer);
      el.removeEventListener('scroll', handleScroll);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('click', onClickCapture, true);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
      if (el.__carouselCleanup === cleanup) el.__carouselCleanup = null;
    };
    el.__carouselCleanup = cleanup;

    return cleanup;
  }, [services.length]);

  return (
    <section className="services" id="services">
      <div className="container">
        <motion.div
          className="services-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.48 }}
        >
          <h2 className="section-title text-center">Service overview</h2>
          <h3 className="services-subtitle text-center">Telehealth care on your terms</h3>
        </motion.div>

        <div className="services-slider-container">
          <div className="services-slider" ref={scrollRef}>
            {duplicatedServices.map((service, index) => (
              <motion.div
                key={`${service.title}-${index}`}
                className="service-slide"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.36, delay: (index % 6) * 0.05 }}
                whileHover={{ scale: 1.05, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
                onClick={() => service.path && navigate(service.path)}
                style={{
                  cursor: 'pointer',
                  ...(service.backgroundImage && {
                    backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.65) 100%), url(${service.backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }),
                }}
              >
                {!service.backgroundImage && service.icon && (
                  <div className="service-icon-wrapper" style={{ backgroundColor: service.color }}>
                    {service.icon}
                  </div>
                )}
                <div className="service-content">
                  <h4 className="service-title">{service.title}</h4>
                  {service.fee && (
                    <p className="service-fee" style={{ marginTop: '0.35rem', fontSize: '0.95rem', opacity: 0.95 }}>
                      {service.fee} {service.feeDetail}
                    </p>
                  )}
                </div>
                <div className="service-pattern" style={{ backgroundColor: `${service.color}15` }} />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
